import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { contactsApi } from "@/lib/api";
import { useCalls } from "@/lib/calls";
import { mergeCallerBook } from "@/lib/callerBook";
import { parseContactSheet } from "@/lib/excel";
import { localNumberInput, normalizeLocalNumber } from "@/lib/shared";

type Contact = {
  id: string;
  name: string;
  phone: string;
  company?: string;
  email?: string;
  notes?: string;
};

function initials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("") || "?";
}

export default function ContactsPage() {
  const { clickToCall } = useCalls();
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<Contact[]>([]);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [callingId, setCallingId] = useState<string | null>(null);

  async function load(q = query) {
    const list = await contactsApi<Contact[]>(
      `/contacts${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`,
    );
    const rows = Array.isArray(list) ? list : [];
    setRows(rows);
    mergeCallerBook(rows);
  }

  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        await load("");
      } catch (err) {
        if (!stop) setError(err instanceof Error ? err.message : "Could not load contacts");
      }
    })();
    return () => {
      stop = true;
    };
  }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((c) =>
      [c.name, c.phone, c.company, c.email].some((v) =>
        String(v || "").toLowerCase().includes(q),
      ),
    );
  }, [rows, query]);

  function openAdd() {
    setError("");
    setOk("");
    setName("");
    setPhone("");
    setCompany("");
    setAddOpen(true);
  }

  async function saveContact(e: FormEvent) {
    e.preventDefault();
    const ten = localNumberInput(phone);
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    if (ten.length !== 10) {
      setError("Enter a 10-digit mobile number");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await contactsApi<Contact>("/contacts", {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          phone: normalizeLocalNumber(ten),
          company: company.trim() || undefined,
        }),
      });
      mergeCallerBook([
        { name: name.trim(), phone: normalizeLocalNumber(ten), company: company.trim() },
      ]);
      setAddOpen(false);
      setOk("Contact saved");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save contact");
    } finally {
      setBusy(false);
    }
  }

  async function remove(c: Contact) {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    setBusy(true);
    setError("");
    try {
      await contactsApi(`/contacts/${c.id}`, { method: "DELETE" });
      setOk("Contact deleted");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete");
    } finally {
      setBusy(false);
    }
  }

  async function onImport(file: File | null) {
    if (!file) return;
    setBusy(true);
    setError("");
    setOk("");
    try {
      const parsed = await parseContactSheet(file);
      const contacts = parsed
        .map((row) => ({
          name: String(row.name || "").trim(),
          phone: String(row.phone || "").trim(),
          company: String(row.company || "").trim(),
          email: String(row.email || "").trim(),
          notes: String(row.notes || "").trim(),
        }))
        .filter((r) => r.phone);
      if (!contacts.length) throw new Error("No phone numbers found in the file");
      const res = await contactsApi<{ imported: number; updated: number; skipped: number }>(
        "/contacts/import",
        { method: "POST", body: JSON.stringify({ contacts }) },
      );
      setOk(
        `Imported ${res.imported || 0} · updated ${res.updated || 0}${
          res.skipped ? ` · skipped ${res.skipped}` : ""
        }`,
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function dial(c: Contact) {
    setCallingId(c.id);
    setError("");
    try {
      await clickToCall(c.phone, { name: c.name, company: c.company });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Call failed");
    } finally {
      setCallingId(null);
    }
  }

  return (
    <div className="contacts-page">
      <div className="contacts-head">
        <div className="contacts-head-copy">
          <h1>Contacts</h1>
          <p className="muted">{rows.length} in directory</p>
        </div>
        <div className="contacts-head-actions">
          <button
            className="btn ghost btn-tiny"
            type="button"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
          >
            Import Excel
          </button>
          <button className="btn btn-tiny contacts-add-btn" type="button" onClick={openAdd}>
            + Add
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv,.txt,.tsv"
            hidden
            onChange={(e) => void onImport(e.target.files?.[0] || null)}
          />
        </div>
      </div>

      <div className="contacts-toolbar">
        <input
          className="search"
          placeholder="Search name or number…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {error ? <p className="error contacts-msg">{error}</p> : null}
      {ok ? <p className="ok contacts-msg">{ok}</p> : null}

      <section className="panel contacts-panel">
        <div className="contacts-list">
          <div className="contacts-list-head">
            <span>Name</span>
            <span>Phone</span>
            <span>Company</span>
            <span />
          </div>
          {visible.length === 0 ? (
            <div className="contacts-empty muted">
              {rows.length === 0
                ? "No contacts yet. Add one or import from Excel."
                : "No matches."}
            </div>
          ) : (
            visible.map((c) => (
              <div className="contacts-row" key={c.id}>
                <div className="contacts-who">
                  <span className="contacts-avatar" aria-hidden>
                    {initials(c.name)}
                  </span>
                  <div>
                    <b>{c.name}</b>
                    {c.email ? <span className="muted contacts-email">{c.email}</span> : null}
                  </div>
                </div>
                <span className="contacts-phone">{c.phone}</span>
                <span className="muted contacts-company">{c.company || "—"}</span>
                <div className="contacts-row-actions">
                  <button
                    className="btn mint btn-tiny"
                    type="button"
                    disabled={busy || callingId === c.id}
                    onClick={() => void dial(c)}
                  >
                    {callingId === c.id ? "…" : "Call"}
                  </button>
                  <button
                    className="btn ghost btn-tiny"
                    type="button"
                    disabled={busy}
                    onClick={() => void remove(c)}
                  >
                    Del
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {addOpen ? (
        <div className="modal-back" onClick={() => setAddOpen(false)}>
          <form
            className="modal contacts-modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => void saveContact(e)}
          >
            <div className="contacts-modal-head">
              <h3>Add contact</h3>
              <button type="button" className="icon-btn" onClick={() => setAddOpen(false)}>
                ×
              </button>
            </div>
            <label className="field">
              <span>Name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                required
                autoFocus
              />
            </label>
            <label className="field">
              <span>Mobile</span>
              <input
                value={phone}
                onChange={(e) => setPhone(localNumberInput(e.target.value))}
                placeholder="10-digit mobile"
                inputMode="numeric"
                required
              />
            </label>
            <label className="field">
              <span>Company</span>
              <input
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Optional"
              />
            </label>
            {error ? <p className="error">{error}</p> : null}
            <div className="btn-row contacts-modal-actions">
              <button className="btn ghost" type="button" onClick={() => setAddOpen(false)}>
                Cancel
              </button>
              <button className="btn" type="submit" disabled={busy}>
                Save
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}
